import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ChatPanel from "../../../components/chat/ChatPanel";
import PresentationPlanView from "./detailed/PresentationPlanViewWithContext";
import VersionHistorySidebar from "./detailed/VersionHistorySidebar";
import ChangeTracker from "./detailed/ChangeTracker";
import { useSlideOperations } from "../context/InsertedSlidesContext";
import { useChangeTracking } from "../context/ChangeTrackingContext";
import { usePlanning } from "../context/PlanningContext";
import { usePlanningMessageEnhancer } from "../../../hooks/usePlanningMessageEnhancer";
import { ChevronIcon, XIcon, ClockIcon, PowerPointIcon } from "../../../icons";
import { useConversation } from "../../../context/ConversationContext";
const plannerWindow = window;
const DetailedPlanning = ({ planData, onBack: _onBack, onBuildStarted, onBuildQueued, initialPrompt, initialPlan, savedVersions, }) => {
    const [messages, setMessages] = useState([]);
    const clearSectionTrackingRef = React.useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isBuildingPlan, setIsBuildingPlan] = useState(false);
    const [buildError, setBuildError] = useState(null);
    // Convert ChatMessage format to Message format for ChatPanel
    const convertMessagesToMessageFormat = useMemo(() => {
        return messages.map((msg) => {
            const baseMessage = {
                id: msg.id,
                role: msg.sender === "user" ? "user" : "assistant",
                content: msg.text,
                timestamp: new Date(msg.timestamp),
                sender: msg.sender,
            };
            // Add step message fields as additional properties
            if (msg.isStepMessage) {
                baseMessage.isStepMessage = true;
                baseMessage.stepLabel = msg.stepLabel;
                baseMessage.stepValue = msg.stepValue;
            }
            // Add tool calls for assistant messages
            if (msg.sender === "assistant" && msg.toolCalls) {
                baseMessage.toolCalls = msg.toolCalls;
            }
            // Add streaming state
            if (msg.isStreaming) {
                baseMessage.isStreaming = true;
            }
            return baseMessage;
        });
    }, [messages]);
    const [currentPlan, setCurrentPlan] = useState(initialPlan || null);
    const [showVersionHistory, setShowVersionHistory] = useState(false); // OFF by default
    const [expandedSections, setExpandedSections] = useState([]);
    // Initialize version history from saved versions if available, otherwise from initial plan
    const initializeVersions = () => {
        if (savedVersions && savedVersions.length > 0) {
            // Convert saved versions to VersionEntry format
            // Use the saved label from backend if available, otherwise fallback to default labels
            return savedVersions.map((version, index) => ({
                plan: version.plan_json,
                label: version.label ||
                    (index === 0 ? "Initial plan created" : "Plan updated"),
                timestamp: new Date(version.timestamp).getTime(),
            }));
        }
        if (initialPlan) {
            return [
                {
                    plan: initialPlan,
                    label: "Initial plan created",
                    timestamp: initialPlan.metadata?.lastModified || Date.now(),
                },
            ];
        }
        return [];
    };
    const [planVersions, setPlanVersions] = useState(initializeVersions);
    const previousPlanRef = useRef(savedVersions && savedVersions.length > 0
        ? savedVersions[savedVersions.length - 1]
            .plan_json
        : initialPlan || null);
    const hasProcessedInitial = useRef(false);
    // Plan persistence
    const { currentPlanId, savePlanVersion, revertToVersion, createNewPlan, setCurrentPlanId, isSaving, } = usePlanning();
    const hasSavedInitialPlan = useRef(false);
    // Create a new plan on first load if we don't have one
    useEffect(() => {
        const initPlanId = async () => {
            if (!currentPlanId && initialPlan && !hasSavedInitialPlan.current) {
                hasSavedInitialPlan.current = true;
                console.log("[DetailedPlanning] Creating new plan for persistence...");
                const result = await createNewPlan();
                if (result) {
                    console.log("[DetailedPlanning] Plan created:", result.planId);
                    // Generate title from plan description (first few words)
                    let planTitle;
                    if (planData.description) {
                        const words = planData.description
                            .split(/\s+/)
                            .slice(0, 8)
                            .join(" ");
                        planTitle =
                            words.length > 40 ? words.substring(0, 40) + "..." : words;
                    }
                    // Save the initial plan version
                    await savePlanVersion(result.planId, initialPlan, initialPlan.slideCount, "Initial plan created", // Label for version history
                    planTitle);
                }
            }
        };
        initPlanId();
    }, [currentPlanId, initialPlan, createNewPlan, savePlanVersion]);
    // Handle plan update with persistence
    const handlePlanUpdateWithPersistence = useCallback(async (updatedPlan) => {
        // Update local state
        setCurrentPlan(updatedPlan);
        // Check if the plan actually changed from the previous version before adding to history
        const hasActualChanges = hasPlanChanges(previousPlanRef.current, updatedPlan);
        if (hasActualChanges) {
            // Add to version history with meaningful label
            const label = generateVersionLabel(previousPlanRef.current, updatedPlan);
            setPlanVersions((prev) => [
                ...prev,
                {
                    plan: updatedPlan,
                    label,
                    timestamp: updatedPlan.metadata?.lastModified || Date.now(),
                },
            ]);
            previousPlanRef.current = updatedPlan;
            // Persist to backend if we have a plan ID
            if (currentPlanId) {
                console.log("[DetailedPlanning] Saving plan version to backend...");
                const result = await savePlanVersion(currentPlanId, updatedPlan, updatedPlan.slideCount, label);
                if (result) {
                    console.log("[DetailedPlanning] Plan version saved:", result.versionId);
                }
            }
        }
        else {
            console.log("[DetailedPlanning] Skipping version creation - no actual changes from previous version");
            // Still update the previous plan ref to track current state (even without version)
            previousPlanRef.current = updatedPlan;
        }
    }, [currentPlanId, savePlanVersion]);
    // Helper to get slides base64 for live style guides
    const getSlidesBase64ForBuild = useCallback(async (styleGuidedocumentId, styleGuideType) => {
        if (styleGuideType !== "live")
            return null;
        console.log("[DetailedPlanning] Live style guide - getting slides base64 from IndexedDB...");
        const getSlideBase64s = window.__getSlideBase64sForDeck;
        if (typeof getSlideBase64s === "function") {
            const slidesBase64 = await getSlideBase64s(styleGuidedocumentId);
            console.log("[DetailedPlanning] Got slides base64:", slidesBase64?.length || 0, "slides");
            return slidesBase64;
        }
        else {
            console.error("[DetailedPlanning] __getSlideBase64sForDeck not available");
            return null;
        }
    }, []);
    /**
     * Executes a build action (start immediately or queue)
     * Centralizes the common validation, setup, and navigation logic
     *
     * IMPORTANT: This function navigates immediately after initiating the build.
     * It does NOT wait for the build init endpoint to complete.
     * The job will appear in the "In Progress" section via useBuildJobs subscription.
     */
    const executeBuildAction = useCallback(async (windowFnName, actionName) => {
        console.log(`[DetailedPlanning] ${actionName} called!`);
        // Clear any previous errors
        setBuildError(null);
        if (!currentPlan || !currentPlanId) {
            console.error("[DetailedPlanning] Cannot build - no plan or plan ID", {
                currentPlan,
                currentPlanId,
            });
            setBuildError("Cannot start build: Plan not ready. Please try again.");
            return;
        }
        const styleGuidedocumentId = planData.styleGuide?.documentId;
        if (!styleGuidedocumentId) {
            console.error("[DetailedPlanning] Cannot build - no style guide selected");
            setBuildError("Cannot start build: No style guide selected. Please select a style guide first.");
            return;
        }
        // Get slides base64 for live style guides (this is fast, from IndexedDB)
        let slidesBase64 = null;
        try {
            slidesBase64 = await getSlidesBase64ForBuild(styleGuidedocumentId, planData.styleGuide?.type);
            if (planData.styleGuide?.type === "live" &&
                (!slidesBase64 || slidesBase64.length === 0)) {
                console.error("[DetailedPlanning] No slides base64 found for live style guide");
                setBuildError("Cannot start build: Unable to load slides from the live style guide.");
                return;
            }
        }
        catch (error) {
            console.error("[DetailedPlanning] Failed to get slides base64:", error);
            setBuildError("Cannot start build: Failed to load style guide slides.");
            return;
        }
        console.log(`[DetailedPlanning] Executing ${actionName}:`, {
            planId: currentPlanId,
        });
        // Call the appropriate window bridge function
        const windowFn = window[windowFnName];
        if (typeof windowFn !== "function") {
            console.error(`[DetailedPlanning] ${windowFnName} not available on window`);
            setBuildError("Cannot start build: Build service not available. Please try refreshing the add-in.");
            return;
        }
        // Start the build WITHOUT awaiting completion
        // The job will appear in useBuildJobs via subscription immediately
        // This prevents UI from hanging while waiting for backend init
        windowFn(currentPlanId, currentPlan, styleGuidedocumentId, planData.styleGuide?.path, slidesBase64).catch((error) => {
            // Log error but don't block - the error will be visible in the build job status
            console.error(`[DetailedPlanning] ${actionName} background error:`, error);
        });
        console.log(`[DetailedPlanning] ${actionName} initiated, navigating back to start view`);
        // Navigate back to start view IMMEDIATELY (don't wait for build to complete)
        // The build job will show up in the "In Progress" section via subscription
        if (onBuildQueued) {
            console.log("[DetailedPlanning] Calling onBuildQueued to return to start view");
            onBuildQueued();
        }
        else if (onBuildStarted && currentPlanId) {
            // Fallback for backwards compatibility
            onBuildStarted(currentPlanId);
        }
    }, [
        currentPlan,
        currentPlanId,
        planData.styleGuide,
        getSlidesBase64ForBuild,
        onBuildQueued,
        onBuildStarted,
    ]);
    // Handle "Start Building" - starts immediately, pausing any active build
    const handleStartBuildImmediately = useCallback(async () => {
        await executeBuildAction("__startBuildImmediately", "Start Build Immediately");
    }, [executeBuildAction]);
    // Handle "Queue Up" - adds to bottom of queue
    const handleQueueBuild = useCallback(async () => {
        await executeBuildAction("__startBuild", "Queue Build");
    }, [executeBuildAction]);
    // Helper to check if plan has actual changes from previous version
    const hasPlanChanges = (oldPlan, newPlan) => {
        // If no old plan, this is a new plan - always has changes
        if (!oldPlan)
            return true;
        // Compare section counts
        if (oldPlan.sections.length !== newPlan.sections.length)
            return true;
        // Compare slide counts
        const oldSlideCount = oldPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        const newSlideCount = newPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        if (oldSlideCount !== newSlideCount)
            return true;
        // Build maps of sections and slides for comparison
        const oldSectionMap = new Map(oldPlan.sections.map((s) => [s.id, s]));
        const oldSlideMap = new Map(oldPlan.sections.flatMap((s) => s.slides.map((sl) => [sl.id, { slide: sl, sectionId: s.id }])));
        // Check each section in new plan
        for (const newSection of newPlan.sections) {
            const oldSection = oldSectionMap.get(newSection.id);
            // Section doesn't exist in old plan
            if (!oldSection)
                return true;
            // Section properties changed
            if (oldSection.name !== newSection.name ||
                oldSection.purpose !== newSection.purpose) {
                return true;
            }
            // Check slides in section
            if (oldSection.slides.length !== newSection.slides.length)
                return true;
            for (const newSlide of newSection.slides) {
                const oldSlideInfo = oldSlideMap.get(newSlide.id);
                // Slide doesn't exist in old plan or moved to different section
                if (!oldSlideInfo || oldSlideInfo.sectionId !== newSection.id)
                    return true;
                const oldSlide = oldSlideInfo.slide;
                // Slide properties changed
                if (oldSlide.name !== newSlide.name ||
                    oldSlide.purpose !== newSlide.purpose) {
                    return true;
                }
            }
        }
        // Check if any old sections were removed
        const newSectionIds = new Set(newPlan.sections.map((s) => s.id));
        for (const oldSection of oldPlan.sections) {
            if (!newSectionIds.has(oldSection.id))
                return true;
        }
        // No changes detected
        return false;
    };
    // Helper to generate meaningful version label based on changes
    const generateVersionLabel = (oldPlan, newPlan) => {
        if (!oldPlan) {
            return "Slide plan created";
        }
        const changes = [];
        const oldSlideCount = oldPlan.slideCount ||
            oldPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        const newSlideCount = newPlan.slideCount ||
            newPlan.sections.reduce((sum, s) => sum + s.slides.length, 0);
        const oldSectionCount = oldPlan.sections.length;
        const newSectionCount = newPlan.sections.length;
        // Count slides with pending flags (LLM changes)
        let insertedSlides = 0;
        let updatedSlides = 0;
        let deletedSlides = 0;
        let insertedSections = 0;
        let deletedSections = 0;
        newPlan.sections.forEach((section) => {
            if (section._pendingInsertion)
                insertedSections++;
            if (section._pendingDeletion)
                deletedSections++;
            section.slides.forEach((slide) => {
                if (slide._pendingInsertion)
                    insertedSlides++;
                if (slide._pendingUpdate)
                    updatedSlides++;
                if (slide._pendingDeletion)
                    deletedSlides++;
            });
        });
        // Calculate total changes
        const totalSlideChanges = insertedSlides + updatedSlides + deletedSlides;
        const totalSectionChanges = insertedSections + deletedSections;
        // Generate specific label
        if (totalSlideChanges > 0 || totalSectionChanges > 0) {
            if (totalSlideChanges > 0) {
                changes.push(`${totalSlideChanges} slide change${totalSlideChanges > 1 ? "s" : ""} made`);
            }
            if (totalSectionChanges > 0) {
                changes.push(`${totalSectionChanges} section${totalSectionChanges > 1 ? "s" : ""} ${insertedSections > deletedSections ? "added" : "changed"}`);
            }
            return changes.join(", ");
        }
        // Fallback: compare counts
        const slideDiff = newSlideCount - oldSlideCount;
        const sectionDiff = newSectionCount - oldSectionCount;
        if (slideDiff > 0) {
            changes.push(`${slideDiff} slide${slideDiff > 1 ? "s" : ""} added`);
        }
        else if (slideDiff < 0) {
            changes.push(`${Math.abs(slideDiff)} slide${Math.abs(slideDiff) > 1 ? "s" : ""} removed`);
        }
        if (sectionDiff > 0) {
            changes.push(`${sectionDiff} section${sectionDiff > 1 ? "s" : ""} added`);
        }
        else if (sectionDiff < 0) {
            changes.push(`${Math.abs(sectionDiff)} section${Math.abs(sectionDiff) > 1 ? "s" : ""} removed`);
        }
        if (changes.length > 0) {
            return changes.join(", ");
        }
        // If no obvious changes, it might be an edit
        return "Plan updated";
    };
    // Use slide operations context (for ChangeTracker component and test mock operations)
    const { slideOperations, clearSlideOperations, hasUnreportedOperations, addSlideOperation, } = useSlideOperations();
    // Use change tracking context for visual styling of changed sections
    const { markSectionAsUpdated } = useChangeTracking();
    // Set up the message enhancer to inject automated reminders about UI-driven changes
    // This ensures the LLM knows about slides added/updated/deleted through the UI
    usePlanningMessageEnhancer({
        currentPlan,
        includePlanContext: true, // Include the full plan JSON for context
        enabled: true,
    });
    // Use conversation context to add step messages
    const { addMessage, setConversationId } = useConversation();
    // Auto-expand all sections when plan is loaded
    useEffect(() => {
        if (currentPlan && currentPlan.sections.length > 0) {
            setExpandedSections(currentPlan.sections.map((s) => s.id));
        }
    }, [currentPlan]);
    // Mark sections with pending changes for visual styling (green border, changes badge)
    // This reads the _pending* flags already set by the editPlan tool
    const markSectionsWithChanges = React.useCallback((plan) => {
        console.log("[DetailedPlanning] Marking sections with changes for visual styling...");
        plan.sections.forEach((section) => {
            // Check if section or any of its slides have pending changes
            const sectionHasFlags = section._pendingInsertion ||
                section._pendingUpdate ||
                section._pendingDeletion;
            const slidesHaveFlags = section.slides.some((slide) => slide._pendingInsertion ||
                slide._pendingUpdate ||
                slide._pendingDeletion);
            if (sectionHasFlags || slidesHaveFlags) {
                console.log("[DetailedPlanning] Marking section for visual update:", section.id, section.name);
                markSectionAsUpdated(section.id);
            }
        });
    }, [markSectionAsUpdated]);
    // Subscribe to plan updates from the planner service
    useEffect(() => {
        if (!plannerWindow.__registerPlanUpdateCallback) {
            console.log("[DetailedPlanning] Plan update callback not available on window");
            return undefined;
        }
        console.log("[DetailedPlanning] Registering for plan updates...");
        const unsubscribe = plannerWindow.__registerPlanUpdateCallback(async (plan, action) => {
            console.log(`[DetailedPlanning] Received plan ${action}:`, plan);
            // The plan from editPlan tool already has _pending* flags set on changed items
            // Just mark sections for visual styling based on those flags
            if (action === "updated") {
                markSectionsWithChanges(plan);
            }
            else if (action === "created") {
                // For newly created plans, mark all sections for visual styling
                console.log("[DetailedPlanning] New plan created, marking all sections");
                plan.sections.forEach((section) => {
                    markSectionAsUpdated(section.id);
                });
            }
            // Update the current plan (it already has _pending* flags from the tool)
            setCurrentPlan(plan);
            // Add to version history with meaningful label
            const label = generateVersionLabel(previousPlanRef.current, plan);
            setPlanVersions((prev) => [
                ...prev,
                {
                    plan,
                    label,
                    timestamp: plan.metadata?.lastModified || Date.now(),
                },
            ]);
            previousPlanRef.current = plan;
            // Persist to backend - THIS WAS MISSING!
            if (currentPlanId) {
                console.log("[DetailedPlanning] Saving plan version to backend from planner callback...");
                try {
                    const result = await savePlanVersion(currentPlanId, plan, plan.slideCount ||
                        plan.sections.reduce((acc, s) => acc + s.slides.length, 0), label);
                    if (result) {
                        console.log("[DetailedPlanning] Plan version saved from planner callback:", result.versionId, "label:", label);
                    }
                    else {
                        console.warn("[DetailedPlanning] Failed to save plan version from planner callback");
                    }
                }
                catch (error) {
                    console.error("[DetailedPlanning] Error saving plan version from planner callback:", error);
                }
            }
            else {
                console.warn("[DetailedPlanning] No currentPlanId available, cannot save plan version");
            }
        });
        return () => {
            console.log("[DetailedPlanning] Unsubscribing from plan updates");
            unsubscribe();
        };
    }, [
        markSectionsWithChanges,
        markSectionAsUpdated,
        currentPlanId,
        savePlanVersion,
    ]);
    // Toggle section expansion
    const toggleSection = (sectionId) => {
        setExpandedSections((prev) => prev.includes(sectionId)
            ? prev.filter((id) => id !== sectionId)
            : [...prev, sectionId]);
    };
    // Initialize step messages when component mounts
    useEffect(() => {
        const initService = async () => {
            try {
                // If we already have an initial plan (generated in PlanProgress), skip LLM generation
                if (initialPlan) {
                    console.log("[DetailedPlanning] Using pre-generated plan from PlanProgress");
                    // Check if this is a test plan and add mock operations
                    const isTestPlan = initialPrompt?.includes("Test Plan - UI Development (No LLM)");
                    if (isTestPlan) {
                        // Add mock slide operations to demonstrate ChangeTracker UI
                        setTimeout(() => {
                            // Modified slides
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-1-1",
                                slideName: "Welcome & Overview",
                                sectionName: "Introduction",
                                slideNumber: 1,
                                timestamp: Date.now(),
                                previousName: "Welcome",
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-1",
                                slideName: "Key Findings",
                                sectionName: "Main Content",
                                slideNumber: 4,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-2",
                                slideName: "Data Analysis",
                                sectionName: "Main Content",
                                slideNumber: 6,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-3",
                                slideName: "Market Trends",
                                sectionName: "Main Content",
                                slideNumber: 8,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-3-1",
                                slideName: "Proposed Strategy",
                                sectionName: "Recommendations",
                                slideNumber: 21,
                                timestamp: Date.now(),
                            });
                            // Added slides
                            addSlideOperation({
                                operation: "inserted",
                                slideId: "slide-new-1",
                                slideName: "New Market Insights",
                                sectionName: "Main Content",
                                slideNumber: 5,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "inserted",
                                slideId: "slide-new-2",
                                slideName: "Competitive Analysis",
                                sectionName: "Main Content",
                                slideNumber: 11,
                                timestamp: Date.now(),
                            });
                            // Removed slides (using actual slide IDs from the plan)
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-1-2",
                                slideName: "Agenda",
                                sectionName: "Introduction",
                                slideNumber: 2,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-3-2",
                                slideName: "Implementation Plan",
                                sectionName: "Recommendations",
                                slideNumber: 7,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-4-1",
                                slideName: "Summary",
                                sectionName: "Conclusion",
                                slideNumber: 8,
                                timestamp: Date.now(),
                            });
                            console.log("[DetailedPlanning] Added mock slide operations for ChangeTracker UI");
                        }, 2000); // Delay to show after plan is loaded
                    }
                    // Create initial messages showing the steps from PlanProgress
                    const initialMessages = [];
                    // Step 1: Slide count
                    const slideCountMessage = {
                        id: `step-slides-${Date.now()}`,
                        text: "",
                        sender: "user",
                        timestamp: Date.now() - 3000,
                        isStepMessage: true,
                        stepLabel: "Choose number of slides",
                        stepValue: planData.slideCount,
                    };
                    initialMessages.push(slideCountMessage);
                    // Step 2: Style guide
                    const styleGuideMessage = {
                        id: `step-style-${Date.now()}`,
                        text: "",
                        sender: "user",
                        timestamp: Date.now() - 2000,
                        isStepMessage: true,
                        stepLabel: "Choose a style-guide",
                        stepValue: planData.styleGuide?.label || "No style guide selected",
                    };
                    initialMessages.push(styleGuideMessage);
                    // Step 3: Create the plan (the prompt)
                    if (initialPrompt) {
                        const userMessage = {
                            id: `step-plan-${Date.now()}`,
                            text: initialPrompt,
                            sender: "user",
                            timestamp: Date.now() - 1000,
                            isStepMessage: true,
                            stepLabel: "Create the plan",
                            stepValue: "",
                        };
                        initialMessages.push(userMessage);
                    }
                    setMessages(initialMessages);
                    // Also add step messages to ConversationContext for ChatPanel
                    initialMessages.forEach((msg) => {
                        const message = {
                            id: msg.id,
                            role: msg.sender === "user" ? "user" : "assistant",
                            content: msg.text,
                            timestamp: new Date(msg.timestamp),
                            sender: msg.sender,
                        };
                        // Add step message fields
                        if (msg.isStepMessage) {
                            message.isStepMessage = true;
                            message.stepLabel = msg.stepLabel;
                            message.stepValue = msg.stepValue;
                        }
                        addMessage(message);
                    });
                    hasProcessedInitial.current = true;
                    return;
                }
                // Process initial prompt after initialization (only if no initial plan provided)
                if (initialPrompt && !hasProcessedInitial.current) {
                    hasProcessedInitial.current = true;
                    // Check if this is a test plan request (bypass LLM)
                    const isTestPlan = initialPrompt.includes("Test Plan - UI Development (No LLM)");
                    if (isTestPlan) {
                        // Generate mock plan immediately without LLM call
                        console.log("[DetailedPlanning] Generating test plan (bypassing LLM)");
                        const testPlan = generateTestPlan(parseInt(planData.slideCount) || 10);
                        setCurrentPlan(testPlan);
                        setPlanVersions([
                            {
                                plan: testPlan,
                                label: "Slide plan created",
                                timestamp: testPlan.metadata?.lastModified || Date.now(),
                            },
                        ]);
                        previousPlanRef.current = testPlan;
                        // Add mock slide operations to demonstrate ChangeTracker UI
                        setTimeout(() => {
                            // Modified slides
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-1-1",
                                slideName: "Welcome & Overview",
                                sectionName: "Introduction",
                                slideNumber: 1,
                                timestamp: Date.now(),
                                previousName: "Welcome",
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-1",
                                slideName: "Key Findings",
                                sectionName: "Main Content",
                                slideNumber: 4,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-2",
                                slideName: "Data Analysis",
                                sectionName: "Main Content",
                                slideNumber: 6,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-2-3",
                                slideName: "Market Trends",
                                sectionName: "Main Content",
                                slideNumber: 8,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "updated",
                                slideId: "slide-3-1",
                                slideName: "Proposed Strategy",
                                sectionName: "Recommendations",
                                slideNumber: 21,
                                timestamp: Date.now(),
                            });
                            // Added slides
                            addSlideOperation({
                                operation: "inserted",
                                slideId: "slide-new-1",
                                slideName: "New Market Insights",
                                sectionName: "Main Content",
                                slideNumber: 5,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "inserted",
                                slideId: "slide-new-2",
                                slideName: "Competitive Analysis",
                                sectionName: "Main Content",
                                slideNumber: 11,
                                timestamp: Date.now(),
                            });
                            // Removed slides (using actual slide IDs from the plan)
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-1-2",
                                slideName: "Agenda",
                                sectionName: "Introduction",
                                slideNumber: 2,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-3-2",
                                slideName: "Implementation Plan",
                                sectionName: "Recommendations",
                                slideNumber: 7,
                                timestamp: Date.now(),
                            });
                            addSlideOperation({
                                operation: "deleted",
                                slideId: "slide-4-1",
                                slideName: "Summary",
                                sectionName: "Conclusion",
                                slideNumber: 8,
                                timestamp: Date.now(),
                            });
                            console.log("[DetailedPlanning] Added mock slide operations for ChangeTracker UI");
                        }, 2000); // Delay to show after plan is loaded
                        // Create initial messages showing the steps
                        const initialMessages = [];
                        // Step 1: Slide count
                        const slideCountMessage = {
                            id: `step-slides-${Date.now()}`,
                            text: "",
                            sender: "user",
                            timestamp: Date.now() - 3000,
                            isStepMessage: true,
                            stepLabel: "Choose number of slides",
                            stepValue: planData.slideCount,
                        };
                        initialMessages.push(slideCountMessage);
                        // Step 2: Style guide
                        const styleGuideMessage = {
                            id: `step-style-${Date.now()}`,
                            text: "",
                            sender: "user",
                            timestamp: Date.now() - 2000,
                            isStepMessage: true,
                            stepLabel: "Choose a style-guide",
                            stepValue: planData.styleGuide?.label || "No style guide selected",
                        };
                        initialMessages.push(styleGuideMessage);
                        // Step 3: Create the plan
                        const userMessage = {
                            id: `step-plan-${Date.now()}`,
                            text: initialPrompt,
                            sender: "user",
                            timestamp: Date.now() - 1000,
                            isStepMessage: true,
                            stepLabel: "Create the plan",
                            stepValue: "",
                        };
                        initialMessages.push(userMessage);
                        // Add a system message indicating test plan was generated
                        const systemMessage = {
                            id: `system-${Date.now()}`,
                            text: "Generated test plan for UI development. This is a mock plan and did not use the LLM.",
                            sender: "assistant",
                            timestamp: Date.now(),
                        };
                        initialMessages.push(systemMessage);
                        setMessages(initialMessages);
                    }
                    else {
                        // Create initial messages showing the steps from PlanProgress
                        const initialMessages = [];
                        // Step 1: Slide count
                        const slideCountMessage = {
                            id: `step-slides-${Date.now()}`,
                            text: "",
                            sender: "user",
                            timestamp: Date.now() - 3000,
                            isStepMessage: true,
                            stepLabel: "Choose number of slides",
                            stepValue: planData.slideCount,
                        };
                        initialMessages.push(slideCountMessage);
                        // Step 2: Style guide
                        const styleGuideMessage = {
                            id: `step-style-${Date.now()}`,
                            text: "",
                            sender: "user",
                            timestamp: Date.now() - 2000,
                            isStepMessage: true,
                            stepLabel: "Choose a style-guide",
                            stepValue: planData.styleGuide?.label || "No style guide selected",
                        };
                        initialMessages.push(styleGuideMessage);
                        // Step 3: Create the plan (the prompt)
                        if (initialPrompt) {
                            const userMessage = {
                                id: `step-plan-${Date.now()}`,
                                text: initialPrompt,
                                sender: "user",
                                timestamp: Date.now() - 1000,
                                isStepMessage: true,
                                stepLabel: "Create the plan",
                                stepValue: "",
                            };
                            initialMessages.push(userMessage);
                        }
                        setMessages(initialMessages);
                        // Initial prompt will be handled by backend through ChatPanel
                        // No need to call handleSendMessage - backend hooks handle it
                    }
                }
            }
            catch (error) {
                console.error("[DetailedPlanning] Failed to initialize LLM service:", error);
            }
        };
        initService();
    }, []);
    // Generate a test plan for quick UI development without LLM calls
    const generateTestPlan = (targetSlideCount) => {
        const testPlan = {
            id: `test-plan-${Date.now()}`,
            slideCount: targetSlideCount,
            version: 1,
            sections: [
                {
                    id: "section-1",
                    name: "Introduction",
                    purpose: "Opening section with background and context",
                    isCollapsed: false,
                    order: 0,
                    slides: [
                        {
                            id: "slide-1-1",
                            name: "Welcome & Overview",
                            purpose: "Introduce the presentation and set expectations",
                            order: 0,
                        },
                        {
                            id: "slide-1-2",
                            name: "Agenda",
                            purpose: "Outline key topics we'll cover",
                            order: 1,
                        },
                    ],
                },
                {
                    id: "section-2",
                    name: "Main Content",
                    purpose: "Core information and analysis",
                    isCollapsed: false,
                    order: 1,
                    slides: [
                        {
                            id: "slide-2-1",
                            name: "Key Findings",
                            purpose: "Present the most important insights",
                            order: 0,
                        },
                        {
                            id: "slide-2-2",
                            name: "Data Analysis",
                            purpose: "Deep dive into supporting data",
                            order: 1,
                        },
                        {
                            id: "slide-2-3",
                            name: "Market Trends",
                            purpose: "Show industry trends and patterns",
                            order: 2,
                        },
                    ],
                },
                {
                    id: "section-3",
                    name: "Recommendations",
                    purpose: "Strategic recommendations based on findings",
                    isCollapsed: false,
                    order: 2,
                    slides: [
                        {
                            id: "slide-3-1",
                            name: "Proposed Strategy",
                            purpose: "Outline recommended approach",
                            order: 0,
                        },
                        {
                            id: "slide-3-2",
                            name: "Implementation Plan",
                            purpose: "Steps to execute the strategy",
                            order: 1,
                        },
                    ],
                },
                {
                    id: "section-4",
                    name: "Conclusion",
                    purpose: "Wrap-up and next steps",
                    isCollapsed: false,
                    order: 3,
                    slides: [
                        {
                            id: "slide-4-1",
                            name: "Summary",
                            purpose: "Recap key points",
                            order: 0,
                        },
                        {
                            id: "slide-4-2",
                            name: "Next Steps",
                            purpose: "Define action items and timeline",
                            order: 1,
                        },
                    ],
                },
            ],
            metadata: {
                createdAt: Date.now(),
                lastModified: Date.now(),
            },
        };
        // Adjust slide count to match target
        let currentSlideCount = testPlan.sections.reduce((total, section) => total + section.slides.length, 0);
        if (currentSlideCount < targetSlideCount) {
            // Add more slides to the last section
            const lastSection = testPlan.sections[testPlan.sections.length - 1];
            const slidesToAdd = targetSlideCount - currentSlideCount;
            for (let i = 0; i < slidesToAdd; i++) {
                lastSection.slides.push({
                    id: `slide-extra-${i + 1}`,
                    name: `Additional Slide ${i + 1}`,
                    purpose: `Extra content slide for testing`,
                    order: lastSection.slides.length,
                });
            }
        }
        else if (currentSlideCount > targetSlideCount) {
            // Remove slides from the end
            let slidesToRemove = currentSlideCount - targetSlideCount;
            for (let i = testPlan.sections.length - 1; i >= 0 && slidesToRemove > 0; i--) {
                const section = testPlan.sections[i];
                while (section.slides.length > 1 && slidesToRemove > 0) {
                    section.slides.pop();
                    slidesToRemove--;
                }
            }
        }
        // Recalculate final slide count
        currentSlideCount = testPlan.sections.reduce((total, section) => total + section.slides.length, 0);
        testPlan.slideCount = currentSlideCount;
        return testPlan;
    };
    // handleSendMessage removed - ChatPanel now uses backend hooks directly
    // The backend will handle plan updates through tool calls/components
    return (_jsxs("div", { className: "flex w-full h-full", children: [_jsxs("div", { className: "flex w-[332px] py-6 px-3 flex-col items-start gap-6 self-stretch bg-gray-100", children: [_jsx("p", { className: "text-sm leading-5 text-gray-600", children: "Chat with the AI to make modifications to your plan" }), _jsx("hr", { className: "w-full border-gray-300" }), _jsx(ChatPanel, { className: "flex flex-col flex-1 w-full gap-3 min-h-0" })] }), _jsxs("div", { className: "flex flex-1 mr-3 mb-3 bg-white rounded-xl border border-gray-300 overflow-hidden", children: [_jsxs("div", { className: "flex flex-col flex-1 overflow-hidden relative", children: [_jsxs("div", { className: "flex gap-1 h-6 items-center shrink-0 px-4 pt-6", children: [_jsxs("div", { className: "flex items-center justify-center gap-[3px] px-2 py-0.5 rounded-full border border-gray-300", children: [_jsx("span", { className: "font-bold text-[12px] leading-normal text-gray-600", children: currentPlan
                                                    ? `${currentPlan.slideCount} slides`
                                                    : `${planData.slideCount} slides` }), _jsx(ChevronIcon, { size: 12, color: "#6B7280", direction: "down" })] }), _jsxs("div", { className: "flex items-center justify-center gap-1 px-2 py-0.5 rounded-full border border-gray-300", children: [_jsx(PowerPointIcon, { variant: "small" }), _jsx("span", { className: "font-bold text-[12px] leading-normal text-gray-600 max-w-[200px] truncate", children: planData.styleGuide?.label || "No style guide selected" }), _jsx(XIcon, { size: 12, color: "#6B7280" })] }), _jsx("div", { className: "flex-1" }), !showVersionHistory && (_jsx("button", { onClick: () => setShowVersionHistory(true), "aria-label": "Show version history", className: "flex w-6 h-6 flex-col items-center justify-center gap-2 rounded-lg border border-gray-100 bg-gray-100 hover:opacity-60 transition-opacity cursor-pointer", children: _jsx(ClockIcon, { size: 16, color: "#4B5563" }) }))] }), _jsx("div", { className: "h-px w-full bg-gray-300 mt-6" }), buildError && (_jsxs("div", { className: "flex items-center gap-2 mx-4 mt-3 p-3 rounded-lg bg-red-50 border border-red-200", children: [_jsx("span", { className: "text-red-600 text-[13px] flex-1", children: buildError }), _jsx("button", { onClick: () => setBuildError(null), className: "text-red-400 hover:text-red-600 transition-colors cursor-pointer", "aria-label": "Dismiss error", children: _jsx(XIcon, { size: 16, color: "currentColor" }) })] })), _jsx("div", { className: "flex-1 overflow-y-auto flex flex-col gap-3 pt-3 px-4 pb-6 relative", children: _jsx(PresentationPlanView, { plan: currentPlan, planDescription: planData.description, expandedSections: expandedSections, onToggleSection: toggleSection, onPlanUpdate: handlePlanUpdateWithPersistence, onClearSectionTrackingRef: clearSectionTrackingRef, onStartBuildImmediately: handleStartBuildImmediately, onQueueBuild: handleQueueBuild, isBuildingPlan: isBuildingPlan, currentPlanId: currentPlanId }) }), _jsx("div", { className: "absolute bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200", children: _jsx(ChangeTracker, { currentPlan: currentPlan, onPlanUpdate: handlePlanUpdateWithPersistence, onSaveChanges: () => {
                                        console.log("[DetailedPlanning] Save changes clicked - accepting all changes");
                                    }, onRejectChanges: () => {
                                        console.log("[DetailedPlanning] Reject changes clicked - reverting all changes");
                                    }, onClearSectionTracking: () => {
                                        if (clearSectionTrackingRef.current) {
                                            clearSectionTrackingRef.current();
                                        }
                                    } }) })] }), showVersionHistory && (_jsx(VersionHistorySidebar, { onClose: () => setShowVersionHistory(false), versions: planVersions.map((version, index) => ({
                            id: `${version.plan.id || "version"}-${index}-${version.timestamp}`,
                            label: version.label,
                            timestamp: version.timestamp,
                        })), onRevert: async (versionId) => {
                            // Find the version by ID
                            const versionIndex = planVersions.findIndex((v, index) => `${v.plan.id || "version"}-${index}-${v.timestamp}` ===
                                versionId);
                            if (versionIndex !== -1) {
                                const targetVersion = planVersions[versionIndex];
                                const revertedPlan = JSON.parse(JSON.stringify(targetVersion.plan));
                                // Update current plan to the reverted version
                                setCurrentPlan(revertedPlan);
                                // Remove all versions after the selected version (truncate history locally)
                                setPlanVersions((prev) => prev.slice(0, versionIndex + 1));
                                // Update the previous plan ref
                                previousPlanRef.current = revertedPlan;
                                // Persist the revert to backend - this truncates versions on the server too
                                if (currentPlanId) {
                                    try {
                                        // versionIndex is 0-based, but version_id is 1-based
                                        const versionNumber = versionIndex + 1;
                                        const result = await revertToVersion(currentPlanId, versionNumber);
                                        if (result) {
                                            console.log("[DetailedPlanning] Reverted to version on backend:", result.versionId);
                                        }
                                    }
                                    catch (error) {
                                        console.error("[DetailedPlanning] Failed to revert plan on backend:", error);
                                    }
                                }
                                console.log("[DetailedPlanning] Reverted to version:", targetVersion.label);
                            }
                        } }))] })] }));
};
export default DetailedPlanning;
